'use client';

import './accordion-group.scss';

import * as RdxAccordion from '@radix-ui/react-accordion';
import React, { ElementType, type JSX, ReactNode } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { trackClick } from '@sola/cookies/components/datalayer.js';
import { getTextFromReactNode } from '@sola/utils/react.js';

export interface AccordionItemProps {
  header: string;
  seoLevel?: number;
  children: JSX.Element;
}

export interface AccordionGroupProps {
  items?: AccordionItemProps[];
  variant?: 'default' | 'tabbed';
  onChange?: (index: number) => void;
}

interface AccordionGroupElementProps {
  seoLevel?: number;
  children: ReactNode;
}
type ButtonRef = HTMLButtonElement;
type DivRef = HTMLDivElement;

const AccordionTrigger = React.forwardRef<
  ButtonRef,
  AccordionGroupElementProps
>(({ seoLevel, children }, forwardedRef) => {
  const Comp = (seoLevel ? `h${seoLevel}` : 'div') as ElementType;

  return (
    <RdxAccordion.Header className="AccordionGroup__header" asChild>
      <div>
        <RdxAccordion.Trigger
          className="AccordionGroup__trigger flex align-center justify-between w-full"
          ref={forwardedRef}
          onClick={() => {
            trackClick(
              getTextFromReactNode(children),
              undefined,
              'AccordionGroup__trigger',
            );
          }}
        >
          <Comp className="label-potent label-xl">{children}</Comp>
          <ArkIcon
            iconName="chevron-down"
            className="AccordionGroup__chevron px-snug-sm ml-comfy-xl"
          />
        </RdxAccordion.Trigger>
      </div>
    </RdxAccordion.Header>
  );
});

const AccordionContent = React.forwardRef<DivRef, AccordionGroupElementProps>(
  ({ seoLevel, children }, forwardedRef) => (
    <RdxAccordion.Content
      className="AccordionGroup__content body-sm"
      ref={forwardedRef}
    >
      {children}
    </RdxAccordion.Content>
  ),
);

export const AccordionGroup: React.FC<AccordionGroupProps> = ({
  items,
  variant = 'default',
  onChange,
}) => {
  return (
    <RdxAccordion.Root
      className={`AccordionGroup AccordionGroup--${variant} w-full flex flex-col gap-comfy-sm`}
      type="single"
      defaultValue={`0_${items?.[0]?.header}`}
      onValueChange={(value) => {
        if (!value || !onChange) {
          return;
        }
        const index = value.split('_')[0];
        onChange(Number(index));
      }}
      collapsible
    >
      {items?.map((item, index) => (
        <RdxAccordion.Item
          key={`${index}_${item.header}`}
          className="AccordionGroup__item flex flex-col"
          value={`${index}_${item.header}`}
        >
          <AccordionTrigger seoLevel={item.seoLevel}>
            {item.header}
          </AccordionTrigger>
          <AccordionContent>{item.children}</AccordionContent>
        </RdxAccordion.Item>
      ))}
    </RdxAccordion.Root>
  );
};
